import React, { useEffect, useState } from 'react';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import Grid from '@mui/material/Grid';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getUser } from 'requests/user';
import { IUser } from 'models/user';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useDictionary } from 'models/dictionaryContext';
import { IParsedDictionary, parseDictionary } from 'helpers/dictionary';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { useTranslations } from 'hooks/useTranslations';
import { getRoles } from 'requests/role';
import { useUsers } from 'pages/UsersPage/hooks';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { EButtonVariants } from 'constants/Buttons';
import { EPermission } from 'models/permissions';
import { StyledPageSubTitle } from 'theme/styles';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';

enum EViewUserPageTranslationsKeys {
  editLink = 'userDetails.edit.link',
  deleteLink = 'userDetails.delete.link',
}

export const ResetUserOfflineAuth = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [userDetails, setUserDetails] = useState<IUser | null>();
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [prefixNames, setPrefixNames] = useState<string[]>([]);
  const [userActivated, setUserActivated] = useState<boolean>();
  const { phoneCode, legalEntityPrefix } = useDictionary();
  const { t } = useTranslations();
  const { handleResetOfflineAuthenticator } = useUsers();
  const { setAlert } = useAlert();
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async function getUserDataInit() {
      if (id) {
        const response = await getUser(id);
        if (response) {
          setUserDetails(response);
          setUserActivated(response.active);
          if (response.roleIds && response.roleIds.length) {
            const responseRoles = await getRoles(1, 100, {}, null);
            if (responseRoles) {
              const roles = responseRoles.content
                .map((role) => response.roleIds.includes(role.uniqueId) && role.name)
                .filter((row) => !!row);
              setRoleNames(roles as string[]);
            }
          }
          if (response.prefixGroupIds && response.prefixGroupIds.length) {
            if (legalEntityPrefix) {
              const names = legalEntityPrefix
                .map(
                  (prefix) =>
                    response.prefixGroupIds.includes(prefix.uniqueId.toString()) &&
                    prefix.name,
                )
                .filter((row) => !!row);
              setPrefixNames(names as string[]);
            }
          }
        }
      }
    })();
  }, [id]);

  const parsedPhoneCodeDictionary: IParsedDictionary = parseDictionary(phoneCode, 'name');

  const resetOfflineAuthenticator = async () => {
    if (userDetails) {
      await handleResetOfflineAuthenticator(id);
      setAlert(ActionTypes.SET_USER_ALERT, {
        text: t('userDetails.resetOfflineAuthenticator.success.message'),
        variant: EAlertVariants.success,
      });
    }
    navigate(ERouteLinks.ViewUser.replace(':id', id));
  };

  if (userDetails) {
    return (
      <>
        <Breadcrumbs
          items={[
            {
              label: t('user.breadcrumbs.administration'),
              route: ERouteLinks.Administration,
            },
            { label: t('user.breadcrumbs.users'), route: ERouteLinks.Users },
            {
              label: t('user.breadcrumbs.userDetails'),
              route: ERouteLinks.ViewUser.replace(':id', id),
            },
            t('userDetails.resetOfflineAuthenticator.header'),
          ]}
        />
        <Grid sx={{ rowGap: 2, marginBottom: 3 }}>
          <Grid size={{ xs: 12 }}>
            <StyledPageSubTitle variant="h5">
              {t('userDetails.resetOfflineAuthenticator.header')}
            </StyledPageSubTitle>
          </Grid>
        </Grid>
        <Box sx={{ mb: 4 }}>
          <Alert
            text={t('userDetails.resetOfflineAuthenticator.message.warning')}
            variant={EAlertVariants.warning}
          />
        </Box>
        {userDetails && (
          <DetailsTemplate
            pageType={EDetailsPageTypes.userView}
            title={userDetails.email}
            deletePermissions={[EPermission.MANAGE_USERS]}
            translationsKeys={EViewUserPageTranslationsKeys}
          >
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.firstName.label')}
                </Typography>
                <Typography variant="body2">{userDetails.firstName}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastName.label')}
                </Typography>
                <Typography variant="body2">{userDetails.lastName}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.phoneNumber.label')}
                </Typography>
                <Typography variant="body2">{`${
                  userDetails.phoneNumber &&
                  parsedPhoneCodeDictionary?.[userDetails.phoneNumber.codeDictionaryUniqueId]
                    ? `${
                        parsedPhoneCodeDictionary?.[
                          userDetails.phoneNumber.codeDictionaryUniqueId
                        ]
                      }${userDetails.phoneNumber.value}`
                    : '-'
                }`}</Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.lastLogged.label')}
                </Typography>
                <Typography variant="body2">
                  {formatDate(userDetails.lastLoginDate, longDateFormat)}
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.role.label')}
                </Typography>
                <div>
                  {roleNames.map((roleItem) => (
                    <Chips key={roleItem} text={roleItem} variant={EChipsVariants.primary} />
                  ))}
                </div>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.status.label')}
                </Typography>
                <div>
                  {userDetails.blocked ? (
                    <Chips text={t('users.blocked.label')} variant={EChipsVariants.warning} />
                  ) : (
                    <Chips
                      text={
                        userActivated ? t('users.active.label') : t('users.inactive.label')
                      }
                      variant={userActivated ? EChipsVariants.success : EChipsVariants.error}
                    />
                  )}
                </div>
              </Stack>
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.prefix.label')}
                </Typography>
                <div>
                  {prefixNames.map((item) => (
                    <Chips key={item} text={item} variant={EChipsVariants.primary} />
                  ))}
                </div>
              </Stack>
            </Grid>
          </DetailsTemplate>
        )}
        <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
          <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Button
                fullWidth
                label={t('general.yes.button')}
                id="users_resetOfflineAuth_yes-button"
                variant={EButtonVariants.contained}
                onClick={resetOfflineAuthenticator}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Button
                fullWidth
                label={t('general.no.button')}
                id="users_resetOfflineAuth_no-button"
                variant={EButtonVariants.outlined}
                onClick={() => navigate(-1)}
              />
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <></>;
};
